<template>
    <transition name="fade">
        <section id="reassignment-page">
            <confirmation-panel v-if="reassignment.isComplete"
                :effectiveDate="fullEffectiveDate"
                :newStateAgentCode="targetAgent.stateAgentCode"
                :newBookName="targetAgent.bookName"
                :policyAction="policyAction"
                :previousStateAgentCode="sourceAgent.stateAgentCode"
                :previousBookName="sourceAgent.bookName"
                :policies="reassignment.data.policies"
                returnTo="Search"
                @closeconfirmation="resetChanges">
            </confirmation-panel>
           <div v-else class="search-and-reassign">
                <h1>
                    Policy Reassignment
                </h1>
                <fieldset class="agents-info">
                    <div class="inputs source">
                        <!--
                        <agent-selector
                            label="Current agent:"
                            :agents="sourceAgentList"
                            :activeOnly="false"
                            :currentAgent="sourceAgent"
                            @setagent="setSourceAgent"
                            :value="sourceAgent.text"
                        >
                        </agent-selector>
                        -->
                        <label>
                            <div>Current agent:</div>
                            <input class="source-agent-input"
                                @change="changeSourceAgent"
                                list="source-agent-list"
                                @keyup="maybeLeaveInput($event)"
                                v-model.lazy="inputSourceAgent"/>
                            <datalist id="source-agent-list" v-if="useBrowserAgentList && !isBobEnabled">
                                <option v-for="agent in agentsList" :key="agent.stateAgentCode">{{agent.stateAgentCode}}</option>
                                <option v-for="agent in agentsList" :key="bareAgentCode(agent.stateAgentCode)">{{bareAgentCode(agent.stateAgentCode)}}</option>
                            </datalist>
                            <datalist id="source-agent-list" v-if="useBrowserAgentList && isBobEnabled">
                                <option v-for="agent in agentsListBob" :key="agent.bookName">{{agent.bookName}}</option>
                                <option v-for="agent in agentsListBob" :key="bareAgentCode(agent.bookName)">{{bareAgentCode(agent.bookName)}}</option>
                            </datalist>
                            <div v-show="inputSourceAgent && !sourceAgentText" class="error no-source-agent">Agent {{inputSourceAgent}} not found</div>
                        </label>
                    </div>
                    <div class="inputs target" :class="{visible: hasSourceAgent}">
                        <label class="target-state-label">
                            <div>New state:</div>
                            <select v-model="selectedTargetStateAbbr" class="target-state-select">
                                <option :value="null" disabled>Select a state</option>
                                <option v-for="s in availableStates" :key="s.value" :value="s.value">{{s.label}}</option>
                            </select>
                            <div v-show="noAgentsInTargetState" class="no-agents-in-state">There are no agents in the {{targetState}}.</div>
                        </label>
                        <agent-selector
                            :key="selectedTargetStateAbbr"
                            :label="`New agent${targetState ? ' ('+targetState+')' :''}:`"
                            :agents="targetAgentList"
                            :activeOnly="true"
                            :distanceFrom="sourceAgent.location"
                            :isBobEnabled="isBobEnabled"
                            @setagent="setTargetAgent"
                            :value="sourceAgent.text">
                        </agent-selector>
                        <span>
                            <label>Effective date:</label><br/>
                            <input v-model="effectiveDate"
                                :max="maxDate"
                                :min="minDate"
                                class="effective-date"
                                title="Policy reassignment effective date"
                                type="date">
                        </span>
                    </div>
                </fieldset>
                <div class="table-label-and-radios">
                    <div class="agent-label" v-show="isBobEnabled ? sourceAgent.bookName : sourceAgent.stateAgentCode">
                        Agent: <strong>{{sourceAgent.text}}</strong>
                    </div>
                    <div role="radiogroup" class="pending-only-toggle" v-if="policies.length > 0 && anyPending">
                        Show
                        <label :class="{selected: !showPendingOnly}">
                            <input type="radio" name="show-all" :checked="!showPendingOnly" :value="false" v-model="showPendingOnly"> Active policies
                        </label>
                        <label :class="{selected: showPendingOnly}">
                            <input type="radio" name="show-all" :checked="showPendingOnly" :value="true" v-model="showPendingOnly"> Pending re-assignment
                        </label>
                    </div>
                </div>
                <div class="policies-list policies-table" v-if="!loading" :class="{pending: showPendingOnly, empty: policies.length===0}">
                    <active-policies-table v-if="!showPendingOnly"
                        @updatepolicy="updatePolicy"
                        :policies="visiblePolicies"
                        :sourceAgent="sourceAgent">
                    </active-policies-table>
                    <pending-policies-table v-if="showPendingOnly"
                        @updatepolicy="updatePolicy"
                        :policies="visiblePolicies"
                        :effectiveDate="fullEffectiveDate"
                        :newAgentCode="targetAgent.stateAgentCode || ''"
                        :sourceAgent="sourceAgent">
                    </pending-policies-table>
                </div>
                <div v-if="policies.length > 0 && !showPendingOnly" class="action-buttons">
                    <button class="btn std reset" title="Reset all pending changes" @click="resetChanges">Reset</button>
                    <button class="btn std primary submit-changes" title="Send reassignments for processing" :aria-disabled="!okToSubmit('isAssigned')" @click.stop.prevent="maybeSubmit">Submit</button>
                </div>
                <div v-if="policies.length > 0 && showPendingOnly" class="action-buttons">
                    <button class="btn std reset" title="Reset all pending changes" @click="resetChanges">Reset</button>
                    <button class="btn std primary submit-changes" title="Cancel pending reassignments" :aria-disabled="!okToAbandon('')" @click.stop.prevent="maybeAbandon">Cancel reassignments</button>
                    <button class="btn std primary submit-changes" title="Send reassignments for processing" :aria-disabled="!okToSubmit('isChecked')" @click.stop.prevent="maybeUpdate">Update reassignments</button>
                </div>
            </div>
            <acknowledgement-modal
                :model="acknowledgement.isOpen"
                :policyAction="policyAction"
                :policies="acknowledgement.policies"
                :sourceAgent="sourceAgent"
                :targetAgent="targetAgent"
                :effectiveDate="fullEffectiveDate"
                @closeacknowledgement="closeAcknowledgement"
            ></acknowledgement-modal>
        </section>
    </transition>
</template>

<script>
import {Net,Urls} from '@/utilities/Net';
import {Utils} from '@/utilities/Utils';
import activePoliciesTable from '@/Components/activePoliciesTable';
import pendingPoliciesTable from '@/Components/pendingPoliciesTable';
import agentSelector from '@/Components/agentSelector';
import acknowledgementModal from '@/Components/acknowledgementModal';
import confirmationPanel from '@/Components/confirmationPanel';

const {stateIds,stateCode,stateName} = Utils,
    cloneObject = (obj) => JSON.parse(JSON.stringify(obj)),
    EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

export default {
    name: 'policy-search',
    components: {
        acknowledgementModal,
        activePoliciesTable,
        agentSelector,
        confirmationPanel,
        pendingPoliciesTable,
    },
    data: function() {
        return {
            acknowledgement: {
                isOpen: false,
                policies: [],
            },

            effectiveDate: '',
            loading: false,
            originalPolicies: [],
            policies: [],

            updateCnt: 1,

            policyAction: '',

            reassignment: {
                isComplete: false,
                policies: [],
            },
            showPendingOnly: false,
            inputSourceAgent: '',
            sourceAgent: {},
            sourceAgentText: '',
            targetAgent: {},
            targetAgentText: '',

            useBrowserAgentList: localStorage.useBrowserAgentList === 'true',
            selectedTargetStateAbbr: null,
            noAgentsInTargetState: false,
        };
    },
    mounted: function() {
        const vue = this;
        vue.fetchBobFeatureToggle().finally(() => vue.refresh());
    },
    computed: {
        anyPending: function() {
            const vue = this;
            return vue.policies.some(policy => policy.pendingReassignPolicyRequestId !== EMPTY_GUID);
        },
        hasSourceAgent: function() {
            const vue = this;
            return Boolean(vue.isBobEnabled ? vue.sourceAgent.bookName : vue.sourceAgent.stateAgentCode);
        },
        visiblePolicies: function() {
            const vue = this,
                policies = vue.policies;
            return policies.filter(p => vue.showPendingOnly === Boolean(p.pendingReassignPolicyRequestId));
        },
        targetState: function() {
            const vue = this;
            return stateName(vue.selectedTargetStateAbbr);
        },
        availableStates: function() {
            return Utils.allStates;
        },
        sourceAgentList: function() {
            const vue = this,
                agentsList = vue.agentsList;

            return agentsList;
        },
        haveSourceAgent: function() {
            const vue = this,
                haveAgent = agt => Boolean((agt || {}).stateAgentCode);
            return haveAgent(vue.sourceAgent);
        },
        allPoliciesSelected: function() {
            const vue = this;
            return vue.policies.every(policy => policy.isAssigned);
        },
        fullEffectiveDate: function() {
            const vue = this,
                today = new Date(),
                timeNow = today.toTimeString().slice(0,8),
                effDate = vue.effectiveDate || today.toISOString().slice(0,10);

            return (new Date(`${effDate}T${timeNow}`)).toISOString();
        },
        targetAgentList: function() {
            const vue = this,
                selectedAbbr = vue.selectedTargetStateAbbr;

            if (!selectedAbbr) return [];

            if (vue.isBobEnabled) {
                const sourceBookName = (vue.sourceAgent || {}).bookName;
                return vue.agentsListBob.filter(agt =>
                    stateIds[stateCode(agt.bookName)] === selectedAbbr &&
                    agt.bookName !== sourceBookName);
            }

            const sourceAgentCode = (vue.sourceAgent || {}).stateAgentCode;

            return vue.agentsList.filter(agt =>
                stateIds[stateCode(agt.stateAgentCode)] === selectedAbbr &&
                agt.stateAgentCode !== sourceAgentCode);
        },
    },
    methods: {
        bareAgentCode: function(code) {
            return code.replace(/-/g,'');
        },
        changeSourceAgent: function() {
            const vue = this,
                code = vue.inputSourceAgent;

            if (!code) {
                vue.setSourceAgent({});
                return;
            }

            if (vue.isBobEnabled) {
                vue.getAgentByBookName(code)
                    .then(agent => {
                        if (agent && agent.bookName) {
                            vue.inputSourceAgent = agent.bookName;
                        }
                        vue.setSourceAgent(agent || {});
                    });
                return;
            }

            vue.getAgentByCode(code)
                .then(agent => {
                    if (agent && agent.stateAgentCode) {
                        vue.inputSourceAgent = agent.stateAgentCode;
                    }
                    vue.setSourceAgent(agent || {});
                });
        },
        maybeLeaveInput: function(e) {
            if (e.code === 'Enter') {
                e.target.blur();
            }
        },
        okToAbandon: function() {
            const vue = this,
                updateCnt = vue.updateCnt, // hack to notice change to policies[x]
                changes = vue.policies.filter(p => p.isChecked);
            return Boolean(updateCnt && changes.length > 0);
        },
        okToSubmit: function(prop) {
            const vue = this,
                updateCnt = vue.updateCnt, // hack to notice change to policies[x]
                changes = vue.policies.filter(p => p[prop]);
            return Boolean(updateCnt && changes.length > 0 &&
                (vue.isBobEnabled ? vue.targetAgent.bookName : vue.targetAgent.stateAgentCode) &&
                vue.effectiveDate);
        },
        selectAgentFilter: function(opts,searchStr) {
            const search = searchStr.replace(/^(..?)(-)(.*)/,(m,p,dash,s) => `${p}${s}`).toLowerCase();

            return search.length > 0 ?
                opts.filter(agt => Boolean(
                    (agt.stateAgentCode || '').replace(/-/g,'').slice(0,search.length).toLowerCase() === search ||
                    (agt.name || '').toLowerCase().match(search))) :
                opts;
        },
        setSourceAgent: function(agent) {
            const vue = this,
                agt = agent || {};
            vue.sourceAgent = agt;
            vue.sourceAgentText = agt.text || '';
            vue.targetAgent = {};
            vue.targetAgentText = '';
            vue.selectedTargetStateAbbr = vue.isBobEnabled ?
                (stateIds[stateCode(agt.bookName)] || null) :
                (stateIds[stateCode(agt.stateAgentCode)] || null);
        },
        setTargetAgent: function(agent) {
            const vue = this;
            vue.targetAgent = agent;
            vue.targetAgentText = agent.text;
        },
        resetChanges: function() {
            const vue = this;
            vue.policies = cloneObject(vue.originalPolicies);
            vue.policies.forEach(p => p.isChecked = false);
            vue.acknowledgement.isOpen =
                vue.reassignment.isComplete = false;
        },
        submitChanges: function(changes) {
            const vue = this;

            function reassignBodyBob() {
                return {
                    ReassignPolicyRequests: changes.map(pol => ({
                        confirmationNumber: pol.confirmationNumber,
                        effectiveDate: vue.fullEffectiveDate,
                        id: pol.pendingReassignPolicyRequestId,
                        newAgentOfficeId: vue.targetAgent.id,
                        newBookName: vue.targetAgent.bookName,
                        newBookOfBusinessId: vue.targetAgent.bookOfBusinessId,
                        policyNumber: pol.policyNumber,
                        previousAgentOfficeId: vue.sourceAgent.id,
                        previousBookName: vue.sourceAgent.bookName,
                        previousBookOfBusinessId: vue.sourceAgent.bookOfBusinessId,
                        status: 'Pending',
                    })),
                };
            }

            function reassignBodyV1() {
                return {
                    ReassignPolicyRequests: changes.map(pol => ({
                        confirmationNumber: pol.confirmationNumber,
                        effectiveDate: vue.fullEffectiveDate,
                        id: pol.pendingReassignPolicyRequestId,
                        newAgentOfficeId: vue.targetAgent.id,
                        newStateAgentCode: vue.targetAgent.stateAgentCode,
                        policyNumber: pol.policyNumber,
                        previousAgentOfficeId: vue.sourceAgent.id,
                        previousStateAgentCode: vue.sourceAgent.stateAgentCode,
                        status: 'Pending',
                    })),
                };
            }

            const result = vue.policyAction === 'abandon' ?
                Net.put(Urls.cancelRequests(), {
                    pendingReassignPolicyRequestIds: changes.map(pol => pol.pendingReassignPolicyRequestId)
                }) :
                Net.post(Urls.reassignPolicy(), vue.isBobEnabled ? reassignBodyBob() : reassignBodyV1());
            result
                .then(function(data) {
                    const reassignment = vue.reassignment;
                    reassignment.data = data.data[0];
                    reassignment.policies = vue.acknowledgement.policies.slice();
                    vue.acknowledgement.policies = [];
                    reassignment.isComplete = true;
                })
                .catch(err => vue.networkError(err.message));

            return result;
        },
        updatePolicy: function(props) {
            const vue = this,
                policy = vue.policies.find(p => p === props.policy);
            if (policy) {
                policy.isAssigned = props.isAssigned;
                policy.isChecked = props.isChecked;
                vue.updateCnt++;
            }
        },
        maybeSubmit: function() {
            const vue = this;
            if (vue.okToSubmit('isAssigned')) {
                const ack = vue.acknowledgement,
                    changes = vue.policies.filter(p => p.isAssigned);
                ack.policies = changes.map(chg => chg);
                ack.isOpen = true;
                vue.policyAction = 'assign';
            }
        },
        maybeUpdate: function() {
            const vue = this;
            if (vue.okToSubmit('isChecked')) {
                const ack = vue.acknowledgement,
                    changes = vue.policies.filter(p => p.isChecked);
                ack.policies = changes.map(chg => chg);
                ack.isOpen = true;
                vue.policyAction = 'assign';
            }
        },
        maybeAbandon: function() {
            const vue = this;
            if (vue.okToAbandon()) {
                const ack = vue.acknowledgement,
                    changes = vue.policies.filter(p => p.isChecked);
                ack.policies = changes.map(chg => chg);
                ack.isOpen = true;
                vue.policyAction = 'abandon';
            }
        },
        closeAcknowledgement: function(andSubmit) {
            const vue = this,
                ack = vue.acknowledgement;
            ack.isOpen = false;
            if (andSubmit) {
                vue.submitChanges(ack.policies)
                    .finally(function(data) {
                        vue.refresh();
                    });
            }
        },
        refresh: function() {
            const vue = this;

            function refreshPoliciesBob() {
                const agentOfficeId = vue.sourceAgent.id;
                if (!agentOfficeId) {
                    return Promise.reject(new Error('no agent id'));
                }
                const result = Net.post(Urls.getPolicies(),{agentOfficeId,...Utils.fullPage});
                result
                    .then(policies => {
                        const page = policies.data;
                        vue.policies = page.items.map(p => {
                            return {
                                ...p,
                                ...{
                                    isAssigned: Boolean(p.effectiveDate && p.newBookName),
                                    effectiveDate: p.effectiveDate || '',
                                }
                            };
                        });
                        vue.originalPolicies = cloneObject(vue.policies);
                    })
                    .catch(e => {
                        console.error('cannot retrieve policies: ',e);
                        vue.networkError(`cannot retrieve policies: ${e.message}`);
                    });
                return result;
            }

            function refreshPoliciesV1() {
                const stateAgentCode = vue.sourceAgent.stateAgentCode;
                if (!stateAgentCode) {
                    return Promise.reject(new Error('no agent code'));
                }
                const result = Net.post(Urls.getPolicies(),{stateAgentCode,...Utils.fullPage});
                result
                    .then(policies => {
                        const page = policies.data;
                        vue.policies = page.items.map(p => {
                            return {
                                ...p,
                                ...{
                                    isAssigned: Boolean(p.effectiveDate && (p.newAgent || {}).stateAgentCode),
                                    effectiveDate: p.effectiveDate || '',
                                    newAgent: p.newAgent || {},
                                }
                            };
                        });
                        vue.originalPolicies = cloneObject(vue.policies);
                    })
                    .catch(e => {
                        console.error('cannot retrieve policies: ',e);
                        vue.networkError(`cannot retrieve policies: ${e.message}`);
                    });
                return result;
            }

            function refreshPolicies() {
                vue.originalPolicies = [];
                vue.policies = [];
                vue.showPendingOnly = false;
                return vue.isBobEnabled ? refreshPoliciesBob() : refreshPoliciesV1();
            }

            vue.loading = true;
            refreshPolicies()
                .then(() => vue.loading = false)
                .catch(() => false);
        },
    },
    watch: {
        sourceAgent: function() {
            const vue = this;
            vue.refresh();
        },
        selectedTargetStateAbbr: function(abbr) {
            const vue = this;
            vue.targetAgent = {};
            vue.targetAgentText = '';
            vue.noAgentsInTargetState = false;
            if (abbr) {
                const lookup = vue.isBobEnabled ?
                    vue.getAgentsByStateAbbrBob(abbr) :
                    vue.getAgentsByStateAbbr(abbr);
                lookup.then(agents => vue.noAgentsInTargetState = agents.length === 0);
            }
        },
    }
}
</script>

<style lang="scss">
@import "../styles/sfadmin";

#reassignment-page {
    padding-bottom: 1em;
    ::placeholder {
        font-style: italic;
        opacity: 0.7;
    }
    .confirmation,
    h1 {
        margin: 0 auto;
        text-align: center;
    }
    .action-buttons,
    .agents-info {
        border: none;
        margin: 1em;
        width: 95%;
    }
    .source-agent-input {
        border: 1px solid rgba(60,60,60,0.26);
        border-radius: 4px;
        font-size: 1em;
        height: 2em;
        outline: none;
        padding-left: 0.5em;
        width: 19.2em;
    }
    .no-source-agent,
    .no-agents-in-state {
        color: var(--sf_red);
    }
    .pending-only-toggle {
        text-align: center;
        label.selected {
            color: var(--sf_red);
            font-weight: bold;
        }
    }
    .inputs {
        display: flex;
        gap: 2em;
        margin: 0.5em 0 1.2em;
    }
    .inputs.target {
        visibility: hidden;
        &.visible {
            visibility: visible;
        }
    }
    .inputs .effective-date {
        width: 10em;
    }
    .target-state-label {
        display: flex;
        flex-direction: column;
    }
    .target-state-select {
        border: 1px solid rgba(60,60,60,0.26);
        border-radius: 4px;
        font-size: 1em;
        height: 2.4em;
        outline: none;
        padding: 0 0.5em;
        width: 20em;
    }
    .table-label-and-radios {
        display: flex;
        gap: 2em;
        justify-content: left;
        margin-left: 2em;
    }
    .policies-table:not(.pending) {
        .col-pending-agt,
        .col-pending-eff-dt {
            display: none;
        }
    }
    .action-buttons {
        display: flex;
        justify-content: space-around;
        margin-top: 1em;
        padding-bottom: 0.6em;
    }
}
</style>
